import React, { useRef, useState, useEffect, useCallback } from 'react';
import { useProjectFinancials } from '../../hooks/ProjectFinancials/useProjectFinancials';
import { useBudgetData } from '../../hooks/ProjectFinancials/useBudgetData';
import { NavLink, useNavigate } from 'react-router-dom';
import {
  House,
  Swatches,
  MapPin,
  Receipt,
  SignOut,
  CurrencyCircleDollar,
  CalendarBlank,
  FileText,
  ClipboardText,
} from '@phosphor-icons/react';
import { useApp } from '../../contexts/AppContext';
import NotificationBell from '../Notifications/NotificationBell';
import './Sidebar.css';

const MenuItem = ({ item, isOpen, onNavItemClick }) => {
  return (
    <li className="menu-item">
      <NavLink
        to={item.path}
        className={({ isActive }) => `menu-link ${isActive ? 'active' : ''}`}
        end={item.path === '/'}
        onClick={() => {
          if (item.onClick) {
            item.onClick();
          }
          if (window.innerWidth <= 768 && isOpen && onNavItemClick) {
            onNavItemClick();
          }
        }}
      >
        {item.icon}
        <span>{item.title}</span>
      </NavLink>
      {item.subItems && (
        <ul className="sub-menu">
          {item.subItems.map((subItem, index) => (
            <li key={index} className="sub-menu-item">
              <NavLink
                to={subItem.path}
                className={({ isActive }) => `sub-menu-link ${isActive ? 'active' : ''}`}
                onClick={() => {
                  if (subItem.onClick) {
                    subItem.onClick();
                  }
                  if (window.innerWidth <= 768 && isOpen && onNavItemClick) {
                    onNavItemClick();
                  }
                }}
              >
                {subItem.title}
              </NavLink>
            </li>
          ))}
        </ul>
      )}
    </li>
  );
};

const LAST_TAB_KEY = 'lastTab';

const menuItems = [
  {
    title: 'ראשי',
    icon: <House className="menu-icon" size={20} />,
    path: '/overview',
  },
  {
    title: 'ניהול תקציב',
    icon: <CurrencyCircleDollar className="menu-icon" size={20} />,
    path: '/budget',
  },
  //{ title: 'לוח זמנים', path: '/schedule' },
  {
    title: 'כתב כמויות',
    icon: <ClipboardText className="menu-icon" size={20} />,
    path: '/boq',
  },
  {
    title: 'בחירת חומרים',
    icon: <Swatches className="menu-icon" size={20} />,
    path: '/project-settings',
  },
  {
    title: 'חללים',
    icon: <MapPin className="menu-icon" size={20} />,
    path: '/spaces',
    onClick: () => {
      localStorage.setItem(LAST_TAB_KEY, 'items');
      localStorage.setItem(LAST_TAB_KEY, 'windows');
    },
    /*subItems: [
      {
        title: 'חלונות',
        path: '/spaces/windows',
        onClick: () => {
          localStorage.setItem(LAST_TAB_KEY, 'windows');
        },
      },
      {
        title: 'חשמל',
        path: '/spaces/electricity',
        onClick: () => {
          localStorage.setItem(LAST_TAB_KEY, 'electricity');
        },
      }, 
    ],*/
  },
  {
    title: 'ספקים',
    icon: <Receipt className="menu-icon" size={20} />,
    path: '/vendors',
  },
  {
    title: 'קבצים',
    icon: <FileText className="menu-icon" size={20} />,
    path: '/files',
  },
];

const Sidebar = ({ isOpen, onNavItemClick }) => {
  const navigate = useNavigate();
  const { currentProject, projects, projectsLoading, selectProject, userProfile, logout } =
    useApp();

  // Scroll fade detection for menu
  const menuRef = useRef(null);
  const [showMenuFade, setShowMenuFade] = useState(false);

  const handleMenuScroll = useCallback(() => {
    const el = menuRef.current;
    if (!el) return;
    // Show fade if not scrolled to bottom
    const isScrollable = el.scrollHeight > el.clientHeight;
    const isAtBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 8;
    setShowMenuFade(isScrollable && !isAtBottom);
  }, []);

  // Check on mount and when menu items might change
  useEffect(() => {
    handleMenuScroll();
  }, [handleMenuScroll]);

  const handleProjectChange = event => {
    const selectedValue = event.target.value;
    if (selectedValue === 'new') {
      navigate('/wizard?new=true');
    } else {
      selectProject(selectedValue);
    }
  };

  const {
    data: { vendorQuotes, projectItems },
    referenceData: { costTypes },
    includeMonthlyPayments,
    includeEstimatedCosts,
    isLoading,
  } = useBudgetData(currentProject?.id);

  const { budgetUsed, estimatedCompletion } = useProjectFinancials({
    vendorQuotes,
    projectItems,
    costTypes,
    currentProject,
    includeMonthlyPayments,
    includeEstimatedCosts,
  });

  return (
    <nav className={`sidebar ${isOpen ? 'open' : ''}`}>
      <div className="sidebar-header">
        <div className="project-section">
          {projectsLoading ? (
            <span className="project-loading">טוען פרוייקט...</span>
          ) : (
            <>
              <span className="project-label">פרוייקט</span>
              <select
                value={currentProject?.id || ''}
                onChange={handleProjectChange}
                className="project-select"
              >
                <option value="">בחר פרויקט</option>
                {projects?.map(project => (
                  <option key={project.id} value={project.id}>
                    {project.address}
                  </option>
                ))}
                <option value="new">+ הוסף פרויקט חדש</option>
              </select>
            </>
          )}
        </div>
      </div>

      <div className="menu-wrapper">
        <ul className="menu" ref={menuRef} onScroll={handleMenuScroll}>
          {menuItems.map((item, index) => (
            <MenuItem key={index} item={item} isOpen={isOpen} onNavItemClick={onNavItemClick} />
          ))}
        </ul>
        <div className={`menu-fade ${showMenuFade ? 'visible' : ''}`} />
      </div>

      {currentProject && (
        <div className="project-financials">
          <div className="financial-item">
            <CurrencyCircleDollar className="financial-icon" size={20} />
            <div className="financial-content">
              <div className="financial-label">תקציב בשימוש</div>
              <div className="financial-value">
                {isLoading ? 'טוען...' : `₪${budgetUsed?.toLocaleString() || '0'}`}
              </div>
            </div>
          </div>
          <div className="financial-item">
            <CalendarBlank className="financial-icon" size={20} />
            <div className="financial-content">
              <div className="financial-label">תאריך סיום משוער</div>
              <div className="financial-value">
                {isLoading
                  ? 'טוען...'
                  : estimatedCompletion
                    ? new Date(estimatedCompletion).toLocaleDateString('he-IL')
                    : 'לא נקבע'}
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="sidebar-footer">
        <div className="footer-content">
          <div className="logout-section">
            <NotificationBell />
            <span className="greeting-text">שלום, {userProfile?.full_name}</span>
            <button
              className="logout-icon-button"
              onClick={logout}
              title="יציאה מהחשבון"
              aria-label="יציאה מהחשבון"
            >
              <SignOut size={20} />
            </button>
          </div>
          <hr className="footer-divider" />
          <div className="logo">KAMUYOT</div>
        </div>
      </div>
    </nav>
  );
};

export default Sidebar;
